import React from "react";

import { getRowStartPositions } from "../../../components/Print/engine/sheetLayout";
import { Btn, Icon, cx } from "../../catalogProductStudio/edit/StudioUi";
import { fill } from "../printCenterModel";

/**
 * Where on the sheet in the printer printing starts. The exact sticker is
 * authoritative; row shortcuts are a convenience; the per-device "last sheet"
 * suggestion is only ever applied by a click.
 */
function SheetStartControls({ tt, geometry, plan, startPosition, slotsPerPage, onChangeStart, suggestion }) {
  const rowStarts = getRowStartPositions(geometry.columns, geometry.rows);
  const firstPage = plan?.pages?.[0];
  const showSuggestion = suggestion && suggestion.nextFreePosition !== startPosition;

  let placement = tt("startEmpty");
  if (plan?.labelCount) {
    placement =
      plan.labelsAfterFirstSheet > 0
        ? fill(tt(plan.pageCount === 2 ? "startOverflowOne" : "startOverflowMany"), {
            first: plan.labelsOnFirstSheet,
            rest: plan.labelsAfterFirstSheet,
            pages: plan.pageCount - 1,
          })
        : fill(tt("startRange"), { from: firstPage.firstPosition, to: firstPage.lastPosition });
  }
  const after = plan?.labelCount
    ? plan.nextFreePosition != null
      ? fill(tt("startNextFree"), { position: plan.nextFreePosition, page: plan.pageCount })
      : tt("startSheetUsedUp")
    : "";

  return (
    <section className="pc-start" aria-labelledby="pc-start-title">
      <div className="pc-start__head">
        <div>
          <h3 id="pc-start-title" className="pc-section-title">
            {tt("startTitle")}
          </h3>
          <p className="pc-muted pc-small">{tt("startCopy")}</p>
        </div>
        <div className="pc-start__position">
          <label htmlFor="pc-start-input" className="pc-eyebrow-label">
            {tt("startAt")}
          </label>
          <div className="pc-start__stepper">
            <button
              type="button"
              className="pse-stepper__btn"
              aria-label={tt("startPrevious")}
              disabled={startPosition <= 1}
              onClick={() => onChangeStart(startPosition - 1)}
            >
              <Icon name="minus" size={15} strokeWidth={2.2} />
            </button>
            <input
              id="pc-start-input"
              type="number"
              min={1}
              max={slotsPerPage}
              value={startPosition}
              onChange={(event) => onChangeStart(event.target.value)}
            />
            <button
              type="button"
              className="pse-stepper__btn"
              aria-label={tt("startNext")}
              disabled={startPosition >= slotsPerPage}
              onClick={() => onChangeStart(startPosition + 1)}
            >
              <Icon name="plus" size={15} strokeWidth={2.2} />
            </button>
          </div>
          <span className="pc-muted pc-small">{fill(tt("startOf"), { count: slotsPerPage })}</span>
        </div>
      </div>

      <div className="pc-start__rows" role="group" aria-label={tt("startRows")}>
        {rowStarts.map(({ row, position }) => (
          <button
            key={row}
            type="button"
            className={cx("pc-chip-btn", startPosition === position && "is-active")}
            aria-pressed={startPosition === position}
            onClick={() => onChangeStart(position)}
          >
            {fill(tt("startRow"), { row })}
          </button>
        ))}
      </div>

      {showSuggestion ? (
        <div className="pc-start__suggestion">
          <Icon name="history" size={18} />
          <div>
            <strong>{fill(tt("suggestion"), { last: suggestion.lastPosition })}</strong>
            <span className="pc-muted pc-small">{tt("suggestionNote")}</span>
          </div>
          <Btn size="sm" onClick={() => onChangeStart(suggestion.nextFreePosition)}>
            {fill(tt("suggestionAction"), { position: suggestion.nextFreePosition })}
          </Btn>
        </div>
      ) : null}

      <div className="pc-start__summary" aria-live="polite">
        <strong>{placement}</strong>
        {after ? <span>{after}</span> : null}
      </div>
    </section>
  );
}

export default SheetStartControls;
